import React, { useCallback, useEffect, useState } from 'react';
import { Button, EmptyState, Spinner, Timeline, useToast } from '../../../ui/components';
import { Icon } from '../../../ui/icons';
import { useT } from '../../../i18n';
import { suppliersApi } from '../suppliersApi';
import { formatDateTime } from './utils';

const PAGE_SIZE = 25;

const META = {
  Created: { tone: 'success', icon: <Icon.Sparkles size={14} /> },
  Updated: { tone: 'default', icon: <Icon.Pencil size={14} /> },
  StatusChanged: { tone: 'warning', icon: <Icon.Activity size={14} /> },
  ContactAdded: { tone: 'primary', icon: <Icon.User size={14} /> },
  ContactUpdated: { tone: 'default', icon: <Icon.User size={14} /> },
  ContactRemoved: { tone: 'danger', icon: <Icon.User size={14} /> },
  AddressAdded: { tone: 'primary', icon: <Icon.MapPin size={14} /> },
  AddressUpdated: { tone: 'default', icon: <Icon.MapPin size={14} /> },
  AddressRemoved: { tone: 'danger', icon: <Icon.MapPin size={14} /> },
  BankAccountAdded: { tone: 'primary', icon: <Icon.Landmark size={14} /> },
  BankAccountUpdated: { tone: 'default', icon: <Icon.Landmark size={14} /> },
  BankAccountRemoved: { tone: 'danger', icon: <Icon.Landmark size={14} /> },
  DocumentAdded: { tone: 'primary', icon: <Icon.FileText size={14} /> },
  DocumentRemoved: { tone: 'danger', icon: <Icon.FileText size={14} /> },
  NoteAdded: { tone: 'primary', icon: <Icon.Bookmark size={14} /> },
  NoteUpdated: { tone: 'default', icon: <Icon.Bookmark size={14} /> },
  NoteRemoved: { tone: 'danger', icon: <Icon.Bookmark size={14} /> },
};

export default function ActivityTab({ supplierId }) {
  const toast = useToast();
  const t = useT();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (nextPage) => {
    setLoading(true);
    try {
      const res = await suppliersApi.listActivity(supplierId, { page: nextPage, pageSize: PAGE_SIZE });
      setTotal(res.totalCount);
      setItems((prev) => (nextPage === 1 ? res.items : [...prev, ...res.items]));
      setPage(nextPage);
    } catch (err) {
      toast.error(t('Could not load activity'), { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [supplierId, toast, t]);

  useEffect(() => { load(1); }, [load]);

  const timelineItems = items.map((a) => {
    const meta = META[a.type] || { tone: 'default', icon: <Icon.Activity size={14} /> };
    return {
      id: a.id,
      tone: meta.tone,
      icon: meta.icon,
      title: a.summary,
      meta: formatDateTime(a.createdAt),
      description: a.actorName ? t('by {name}', { name: a.actorName }) : undefined,
    };
  });

  if (loading && items.length === 0) {
    return <div className="sp-center"><Spinner size={24} /></div>;
  }

  if (items.length === 0) {
    return <EmptyState icon={<Icon.Activity size={28} />} title={t('No activity yet')} description={t('Changes to this supplier will appear here.')} />;
  }

  return (
    <div className="sp-panel">
      <Timeline items={timelineItems} />
      {items.length < total && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant="secondary" onClick={() => load(page + 1)} loading={loading}>{t('Load more')}</Button>
        </div>
      )}
    </div>
  );
}
